import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, ArrowRight, Loader2 } from "lucide-react";

/**
 * CaptureGate — Phase OTP-3 reusable capture modal.
 *
 *   <CaptureGate
 *     trigger={<button>Download report</button>}
 *     title="Download your legal report"
 *     submitLabel="Generate & download"
 *     onCapture={async ({ name, email, phone }) => { ... }}
 *   />
 *
 * The host page is responsible for the actual generation logic (PDF, OTP,
 * matter bootstrap etc) — this component is purely the capture envelope.
 * It enforces the platform's "no anonymous downloads" rule.
 */
export default function CaptureGate({
  trigger,
  title       = "Tell us who you are",
  description = "We use these details to save your matter and link any documents we create for you. No anonymous downloads.",
  submitLabel = "Continue",
  onCapture,
  testid      = "capture-gate",
}) {
  const [open, setOpen]     = useState(false);
  const [busy, setBusy]     = useState(false);
  const [form, setForm]     = useState({ name: "", email: "", phone: "" });

  const close = () => {
    setOpen(false);
    setForm({ name: "", email: "", phone: "" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Full name, email and phone are required.");
      return;
    }
    setBusy(true);
    try {
      await onCapture(form);
      close();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <span onClick={() => setOpen(true)}>{trigger}</span>
      <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : close())}>
        <DialogContent className="rounded-none" data-testid={`${testid}-dialog`}>
          <DialogHeader>
            <div className="flex items-center gap-2 text-overline text-zinc-500">
              <ShieldCheck className="h-3 w-3" /> Client capture · POPIA
            </div>
            <DialogTitle className="font-serif text-3xl">{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>

          <form onSubmit={submit} className="space-y-4 pt-2">
            <div>
              <Label htmlFor="cg-name" className="text-xs uppercase tracking-wider text-zinc-500">Full name *</Label>
              <Input id="cg-name" required value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="rounded-none mt-1" data-testid={`${testid}-name`} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="cg-email" className="text-xs uppercase tracking-wider text-zinc-500">Email *</Label>
                <Input id="cg-email" type="email" required value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="rounded-none mt-1" data-testid={`${testid}-email`} />
              </div>
              <div>
                <Label htmlFor="cg-phone" className="text-xs uppercase tracking-wider text-zinc-500">Phone *</Label>
                <Input id="cg-phone" required value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="rounded-none mt-1" data-testid={`${testid}-phone`} />
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-300 px-3 py-2 text-xs text-amber-900 flex items-start gap-2">
              <ShieldCheck className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              <span>By submitting, you create a private matter file with EH Inc Attorneys. Your data is processed under POPIA — we will not share it with third parties.</span>
            </div>

            <Button type="submit" disabled={busy}
              className="w-full rounded-none bg-zinc-900 hover:bg-zinc-800"
              data-testid={`${testid}-submit`}>
              {busy
                ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Working…</>
                : <>{submitLabel} <ArrowRight className="h-4 w-4 ml-1.5" /></>}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
